/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { Provider } from './types'

import type { AwsState } from './AwsProviderForm'
import type { GcpState } from './GcpProviderForm'
import type { DirectState } from './DirectProviderForm'

/**
 * logic-only validation for payment provider forms.
 * Separated from React to allow for exhaustive unit testing of billing rules.
 */
export const isProviderFormValid = ({
  provider,
  awsState,
  gcpState,
  directState,
}: {
  provider: Provider | null
  awsState: AwsState
  gcpState: GcpState
  directState: DirectState
}): boolean => {
  if (!provider) {
    return false
  }

  switch (provider) {
    case Provider.AWS:
      return awsState.account_id.trim().length > 0
    case Provider.GCP:
      return gcpState.billing_account_id.trim().length > 0

    case Provider.DIRECT: {
      const hasCode = directState.activation_code.trim().length > 0
      return hasCode ? !!directState.level : !!directState.start_date
    }

    default:
      return false
  }
}

// Strictly typed payload to ensure backend compatibility
export interface ApiDomainChangePayload {
  aws?: {
    account_id: string
    backdate_on_consumption: string
  }
  gcp?: {
    billing_account_id: string
    backdate_on_consumption: string
  }
  direct?: { activation_code: string; level: string } | { start_date: string }
}

export const mapStateToApiPayload = (
  provider: Provider,
  states: { aws: AwsState; gcp: GcpState; direct: DirectState },
): ApiDomainChangePayload => {
  const payload: ApiDomainChangePayload = {}

  if (provider === Provider.AWS) {
    payload.aws = {
      account_id: states.aws.account_id.trim(),
      backdate_on_consumption: String(states.aws.backdate_on_consumption),
    }
  } else if (provider === Provider.GCP) {
    payload.gcp = {
      billing_account_id: states.gcp.billing_account_id.trim(),
      backdate_on_consumption: String(states.gcp.backdate_on_consumption),
    }
  } else if (provider === Provider.DIRECT) {
    const code = states.direct.activation_code.trim()
    payload.direct = code
      ? { activation_code: code, level: states.direct.level }
      : { start_date: states.direct.start_date }
  }

  return payload
}
